import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { getInt } from 'utils/functions';
import { LEASING_SERVICE_TYPE } from '../../queries';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import FlexBox from 'components/common/FlexBox';
import { IconButton, Typography } from '@totalsoft/rocket-ui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import LeasingServiceTypeGeneralData from './LeasingServiceTypeGeneralData';
import LeasingServiceTypeFormulas from './LeasingServiceTypeFormulas';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_SERVICE_TYPES_WRITE } from 'constants/rightsCodes';
import { SAVE_LEASING_SERVICE_TYPE } from 'features/leasingServiceType/mutations';
import { LEASING_SERVICE_TYPE_SAVED } from 'features/leasingServiceType/subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { greaterThan, required, shape, stopOnFirstFailure } from '@totalsoft/pure-validations';
import Portal from 'utils/portal/Portal';
import { withAreaGetter } from 'providers/AreasConsumers';
import { areas } from 'constants/areas';
import { Prompt } from 'react-router-dom';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';

const validator = shape({
  code: required,
  name: required,
  kindId: required,
  periodicity: [required, greaterThan(0)] |> stopOnFirstFailure
});

const LeasingServiceTypeContainer = ({ getElemByAreaKey }) => {
  const { t } = useTranslation();
  const match = useRouteMatch();
  const historyContext = useHistoryWithContext();
  const addToast = useToast();

  const id = useMemo(() => match.params.id && getInt(match.params.id), [match]);
  const canWrite = usePermission(ADMIN_LEASING_SERVICE_TYPES_WRITE);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const [validation, validate] = useDirtyFieldValidation(validator);

  const [pending, setPending] = useState(false);

  useQuery(LEASING_SERVICE_TYPE, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      resetLens(data.leasingServiceType);
    }
  });

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  // #region Save

  const [saveLeasingServiceType] = useMutation(SAVE_LEASING_SERVICE_TYPE, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    // eslint-disable-next-line no-unused-vars
    const { linkItemName, supplierName, ...sanitizedData } = data;

    setPending(true);

    saveLeasingServiceType({
      variables: { input: sanitizedData }
    });
  };

  useSubscription(LEASING_SERVICE_TYPE_SAVED, {
    onData: ({ client, data }) => {
      resetLens(data.data.leasingServiceTypeSaved.leasingServiceType);
      setPending(false);
      addToast(t('LeasingServiceType.Saved'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingServiceTypes' });
      if (!id) {
        const newId = data.data.leasingServiceTypeSaved.leasingServiceType.id;
        setTimeout(() => {
          historyContext.push(`/leasingServiceTypes/${newId}`);
        }, 100);
      }
    }
  });

  // #endregion

  const handleBack = useCallback(() => {
    historyContext.push('/leasingServiceTypes');
  }, [historyContext]);

  const disabled = useMemo(() => !canWrite || pending, [canWrite, pending]);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  return (
    <FlexBox direction='column'>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Portal container={getElemByAreaKey(areas.header)}>
        <Typography noWrap variant='subtitle1' fontWeight='bold'>
          {t('LeasingServiceType.Title', { name: lens.name |> get })}
        </Typography>
      </Portal>
      <Portal container={getElemByAreaKey(areas.headerLinks)}>
        <FlexBox gap={0.5} mr={0.5}>
          <IconButton tooltip={t('BackToList')} variant='text' color='primary' onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          {canWrite && (
            <IconButton color='primary' variant='text' tooltip={t('Save')} onClick={handleSave} disabled={!isDirty || disabled}>
              <SaveIcon />
            </IconButton>
          )}
        </FlexBox>
      </Portal>
      <LeasingServiceTypeGeneralData lens={lens} validation={validation} disabled={disabled} />
      <LeasingServiceTypeFormulas lens={lens} disabled={disabled} />
    </FlexBox>
  );
};

LeasingServiceTypeContainer.propTypes = {
  getElemByAreaKey: PropTypes.func
};

export default withAreaGetter(LeasingServiceTypeContainer);
